/**
 * The provider list of the preference dialog: one entry per published provider, in a stable
 * localized order, with a short summary taken from its own disclosed purposes, searchable by
 * name and purpose, and shown a page at a time. Pure functions only; nothing here reads or
 * changes consent.
 */

export const PROVIDER_PAGE_SIZE = 5;
/** Longest first sentence still read as a summary rather than the full disclosure. */
const SUMMARY_MAX_LENGTH = 64;

export interface ProviderPurposeInput {
  category: string;
  text: string;
}

export interface ProviderInput {
  id: string;
  name: string;
  privacyPolicyUrl: string | null;
  purposes: ProviderPurposeInput[];
}

export interface ProviderPurpose {
  category: string;
  categoryLabel: string;
  text: string;
}

export interface ProviderModel {
  id: string;
  name: string;
  privacyPolicyUrl: string | null;
  /** A short phrase from the provider's own disclosure, or its category labels. */
  summary: string;
  purposes: ProviderPurpose[];
  /** Normalized name, purposes and category labels, for search. */
  searchText: string;
}

export interface ProviderPage {
  items: ProviderModel[];
  page: number;
  pageCount: number;
  /** Matches across all pages. */
  total: number;
  /** 1-based index of the first item on this page; 0 when there are none. */
  from: number;
  to: number;
}

/** Case-, accent- and locale-insensitive text for matching. */
export function normalizeForSearch(value: string, locale: string): string {
  let lowered: string;
  try {
    lowered = value.toLocaleLowerCase(locale);
  } catch {
    lowered = value.toLowerCase();
  }
  return lowered.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * The provider's first purpose, cut to its first sentence when that is short enough to be a
 * summary. Otherwise the labels of the categories it serves: honest, never invented, and never
 * a truncated sentence.
 */
export function summarizeProvider(purposes: ProviderPurpose[]): string {
  const first = purposes[0]?.text.trim() ?? '';
  const sentence = /^(.+?[.!?。؟])(?:\s|$)/u.exec(first)?.[1] ?? first;
  const phrase = sentence.replace(/[.。]$/u, '').trim();
  if (phrase && phrase.length <= SUMMARY_MAX_LENGTH) return phrase;
  return [...new Set(purposes.map((purpose) => purpose.categoryLabel))].join(' · ');
}

/**
 * Providers with category labels attached, sorted by name for the reader's language and then
 * by id, so the order is the same on every render.
 */
export function buildProviderModels(
  providers: readonly ProviderInput[],
  categoryLabels: Readonly<Record<string, string>>,
  categoryOrder: readonly string[],
  locale: string
): ProviderModel[] {
  let collator: Intl.Collator;
  try {
    collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  } catch {
    collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
  }
  return providers
    .map((provider) => {
      const purposes = [...provider.purposes]
        .sort((left, right) => categoryOrder.indexOf(left.category) - categoryOrder.indexOf(right.category))
        .map((purpose) => ({
          category: purpose.category,
          categoryLabel: categoryLabels[purpose.category] ?? purpose.category,
          text: purpose.text,
        }));
      return {
        id: provider.id,
        name: provider.name,
        privacyPolicyUrl: provider.privacyPolicyUrl,
        summary: summarizeProvider(purposes),
        purposes,
        searchText: normalizeForSearch(
          [provider.name, ...purposes.flatMap((purpose) => [purpose.categoryLabel, purpose.text])].join(' '),
          locale
        ),
      };
    })
    .sort((left, right) => collator.compare(left.name, right.name) || (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
}

/** Every word of the query must appear somewhere in the provider's name or purposes. */
export function filterProviders(models: readonly ProviderModel[], query: string, locale: string): ProviderModel[] {
  const words = normalizeForSearch(query, locale).split(' ').filter(Boolean);
  if (words.length === 0) return [...models];
  return models.filter((model) => words.every((word) => model.searchText.includes(word)));
}

/** One page of matches; an out-of-range page is clamped rather than shown empty. */
export function pageProviders(models: readonly ProviderModel[], requestedPage: number): ProviderPage {
  const total = models.length;
  const pageCount = Math.max(1, Math.ceil(total / PROVIDER_PAGE_SIZE));
  const page = Math.min(Math.max(0, Math.trunc(requestedPage) || 0), pageCount - 1);
  const items = models.slice(page * PROVIDER_PAGE_SIZE, page * PROVIDER_PAGE_SIZE + PROVIDER_PAGE_SIZE);
  return {
    items,
    page,
    pageCount,
    total,
    from: items.length ? page * PROVIDER_PAGE_SIZE + 1 : 0,
    to: page * PROVIDER_PAGE_SIZE + items.length,
  };
}
